/**
 * Helpers pour le Codex : récupérer les fiches selon le nœud, les tags, l'ID ou le contexte de correction.
 */

import { CODEX_ENTRIES } from '../data/codexEntries'
import { PARCOURS_NODE_ORDER, STAGE_LABELS, getNodeDef } from '../data/parcoursTree'

/** Mapping fonction (T, SD, D) → tag pour la recherche de fiches */
const FUNCTION_TO_TAG = {
  T: 'Tonique',
  SD: 'SousDominante',
  D: 'Dominante'
}

/** Mapping valeur cadence (stockée dans chord.cadence) → tag */
const CADENCE_VALUE_TO_TAG = {
  'perfect': 'CadenceParfaite',
  'imperfect': 'CadenceImparfaite',
  'plagal': 'CadencePlagale',
  'rompue': 'CadenceRompue',
  'demi-cadence': 'DemiCadence',
  'half': 'DemiCadence',
  'Cadence6/4': 'Cadence6/4'
}

/**
 * Retourne les fiches dont relatedNodeIds contient nodeId.
 * @param {string} nodeId - ID du nœud parcours (ex. '1.1', 'cadence-parfaite')
 * @returns {Array<object>}
 */
export function getCodexEntriesForNode(nodeId, entries = CODEX_ENTRIES) {
  if (!nodeId || typeof nodeId !== 'string') return []
  return entries.filter(
    (entry) => Array.isArray(entry.relatedNodeIds) && entry.relatedNodeIds.includes(nodeId)
  )
}

/**
 * Lien Codex principal pour un nœud parcours (première fiche liée).
 * @returns {{ ficheId: string, sectionId: string|null }|null}
 */
export function getCodexLinkForNode(nodeId, entries = CODEX_ENTRIES) {
  const list = getCodexEntriesForNode(nodeId, entries)
  if (!list.length) return null
  return { ficheId: list[0].id, sectionId: null }
}

/**
 * Retourne les fiches dont au moins un relatedTag est dans la liste tags.
 * @param {string[]} tags - Tags (ex. ['CadenceParfaite', 'Tonique'])
 * @param {number} [limit] - Nombre max de fiches à retourner
 * @returns {Array<object>}
 */
export function getCodexEntriesForTags(tags, limit = 10, entries = CODEX_ENTRIES) {
  if (!Array.isArray(tags) || tags.length === 0) return []
  const set = new Set(tags.map((t) => (t || '').trim()).filter(Boolean))
  const found = entries.filter(
    (entry) =>
      Array.isArray(entry.relatedTags) &&
      entry.relatedTags.some((tag) => set.has(tag))
  )
  return limit ? found.slice(0, limit) : found
}

/**
 * Retourne une fiche par ID.
 * @param {string} id - ID de la fiche (ex. 'fiche-fonctions-tsd-d')
 * @returns {object|null}
 */
export function getCodexEntryById(id, entries = CODEX_ENTRIES) {
  if (!id || typeof id !== 'string') return null
  return entries.find((entry) => entry.id === id) || null
}

/**
 * Suggère 1 à 2 fiches pertinentes à partir de la réponse correcte (pour le feedback de correction).
 * Priorité : fiches liées au nodeId (mode parcours), puis fonction de l'accord, puis cadence.
 * @param {object} correctChord - Accord correct (degree, selectedFunction, cadence, etc.)
 * @param {string|null} [nodeId] - Nœud parcours si en mode parcours
 * @returns {Array<object>} - 1 ou 2 fiches, sans doublon
 */
export function getCodexEntriesForCorrection(correctChord, nodeId = null, entries = CODEX_ENTRIES) {
  const seen = new Set()
  const result = []

  // 1. Fiches liées au nœud (mode parcours)
  if (nodeId) {
    const forNode = getCodexEntriesForNode(nodeId, entries)
    for (const entry of forNode) {
      if (!seen.has(entry.id)) {
        seen.add(entry.id)
        result.push(entry)
      }
      if (result.length >= 2) return result
    }
  }

  // 2. Fiche liée à la fonction de l'accord
  const func = correctChord?.selectedFunction || (correctChord?.degree && degreeToFunction(correctChord.degree))
  if (func && FUNCTION_TO_TAG[func]) {
    const tag = FUNCTION_TO_TAG[func]
    const forFunc = getCodexEntriesForTags([tag], 1, entries)
    if (forFunc.length > 0 && !seen.has(forFunc[0].id)) {
      seen.add(forFunc[0].id)
      result.push(forFunc[0])
    }
    if (result.length >= 2) return result
  }

  // 3. Fiche liée à la cadence (si l'accord a une cadence attendue)
  const cadence = correctChord?.cadence
  if (cadence) {
    const tag = CADENCE_VALUE_TO_TAG[cadence] || (cadence === 'Cadence6/4' ? 'Cadence6/4' : null)
    if (tag) {
      const forCadence = getCodexEntriesForTags([tag], 1, entries)
      if (forCadence.length > 0 && !seen.has(forCadence[0].id)) {
        seen.add(forCadence[0].id)
        result.push(forCadence[0])
      }
    }
  }

  // 4. Tierce Picarde : I majeur en mineur → fiche dédiée
  if (correctChord?.degree === 'I' && correctChord?.figure === '5') {
    const picarde = getCodexEntryById('fiche-tierce-picarde', entries)
    if (picarde && !seen.has(picarde.id) && result.length < 2) {
      seen.add(picarde.id)
      result.push(picarde)
    }
  }

  // 5. Mouvements obligés : suggestion pour accords de dominante (V, V7) et cadence rompue
  if (correctChord?.degree === 'V' || cadence === 'rompue') {
    const obliges = getCodexEntryById('fiche-mouvements-obliges', entries)
    if (obliges && !seen.has(obliges.id) && result.length < 2) {
      seen.add(obliges.id)
      result.push(obliges)
    }
  }

  return result
}

/** Degré principal → fonction unique pour suggestion (I→T, IV→SD, V→D, etc.) */
const DEGREE_TO_FUNCTION = {
  I: 'T', III: 'T', VI: 'T',
  IV: 'SD', II: 'SD',
  V: 'D', VII: 'D'
}
function degreeToFunction(degree) {
  const d = (degree || '').toString().trim()
  return DEGREE_TO_FUNCTION[d] || null
}

/**
 * Retourne toutes les fiches, groupées par catégorie (pour l'affichage liste).
 * @returns {Record<string, object[]>}
 */
export function getCodexEntriesByCategory(entries = CODEX_ENTRIES) {
  const byCategory = {}
  for (const entry of entries) {
    const cat = entry.category || 'parcours'
    if (!byCategory[cat]) byCategory[cat] = []
    byCategory[cat].push(entry)
  }
  for (const cat of Object.keys(byCategory)) {
    byCategory[cat].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  }
  return byCategory
}

/** Ordre des catégories pour liste plate */
const CATEGORY_ORDER = ['fonctions', 'cadences', 'enchainements', 'renversements', 'couleurs', 'parcours', 'contexte']

/**
 * Retourne toutes les fiches dans l'ordre d'affichage (pour navigation précédent/suivant).
 * @returns {object[]}
 */
export function getCodexEntriesOrdered(entries = CODEX_ENTRIES) {
  const byCategory = getCodexEntriesByCategory(entries)
  const result = []
  for (const cat of CATEGORY_ORDER) {
    if (byCategory[cat]) result.push(...byCategory[cat])
  }
  return result
}

const NODE_ORDER_INDEX = Object.fromEntries(
  PARCOURS_NODE_ORDER.map((id, i) => [id, i])
)

/**
 * Retourne les fiches groupées par phase du parcours (pour la vue « Par parcours »).
 * Chaque fiche est rangée dans la phase de son premier nœud lié dans PARCOURS_NODE_ORDER.
 * Les fiches sans relatedNodeIds ou sans nœud reconnu vont en phase 0 « Hors parcours ».
 * @returns {{ byPhase: Array<{ stage: number, label: string, entries: object[] }>, flatOrder: object[] }}
 */
export function getCodexEntriesByParcours(entries = CODEX_ENTRIES) {
  const byStage = { 0: [] }
  for (let s = 1; s <= 4; s++) byStage[s] = []

  for (const entry of entries) {
    const nodeIds = entry.relatedNodeIds || []
    let minIndex = Infinity
    let firstNodeId = null
    for (const nid of nodeIds) {
      const idx = NODE_ORDER_INDEX[nid]
      if (idx !== undefined && idx < minIndex) {
        minIndex = idx
        firstNodeId = nid
      }
    }
    const stage = firstNodeId ? (getNodeDef(firstNodeId)?.stage ?? 0) : 0
    const list = byStage[stage] ?? byStage[0]
    list.push({ entry, sortIndex: minIndex })
  }

  const byPhase = []
  for (let stage = 1; stage <= 4; stage++) {
    const list = byStage[stage] || []
    list.sort((a, b) => a.sortIndex - b.sortIndex)
    byPhase.push({
      stage,
      label: STAGE_LABELS[stage] || `Phase ${stage}`,
      entries: list.map(({ entry }) => entry)
    })
  }
  const horsParcours = (byStage[0] || []).sort((a, b) => a.sortIndex - b.sortIndex)
  if (horsParcours.length) {
    byPhase.push({
      stage: 0,
      label: 'Hors parcours',
      entries: horsParcours.map(({ entry }) => entry)
    })
  }

  const flatOrder = []
  for (const { entries } of byPhase) flatOrder.push(...entries)

  return { byPhase, flatOrder }
}
